<template>
  <div class="flex items-center justify-center h-screen bg-gray-50">
    <div v-if="errorMessage" class="text-center">
      <p class="text-red-600 text-lg font-semibold">{{ errorHeading }}</p>
      <p class="text-gray-600 mt-2">{{ errorMessage }}</p>
    </div>
    <div v-else class="text-center">
      <div
        class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"
      ></div>
      <p class="text-gray-600 mt-4">{{ loaderMessage }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useCompanyAuthSession } from "@/composables/useCompanyAuthSession";
import {
  clearExpertToCompanyHandoffCookies,
  syncSwitchableCompanyHandoffCookie,
} from "@/cognito/crossAppLogoutCookie";
import { decodeJwtPayload } from "@/cognito/jwtClaims";
import { User } from "@/types/user-types";
import { Company } from "@/types/company-types";

/**
 * /auth/impersonate?token=<impersonation-jwt>
 *
 * Company SPA counterpart of the Expert SPA ImpersonateView.
 * Opened by an admin (via Retool) after POST /admin/impersonate.
 *
 * This view:
 * 1. Reads the HS256 JWT from the query string
 * 2. POSTs to /company/login (VUE_APP_SIGN_IN_URL) — Rails Authentication
 *    concern detects iss=slashexperts-impersonation
 * 3. Hydrates the Pinia user store with the returned company user data
 * 4. Sets isImpersonated = true so the ImpersonationBanner shows
 * 5. Same store hydration, Produktly/PostHog as HomeView, and post-auth routing
 *    (AddCompanyDetails / SignInLanding vs dashboard)
 */
export default defineComponent({
  name: "CompanyImpersonateView",

  setup() {
    const route = useRoute();
    const router = useRouter();
    const {
      userStore,
      applyCompanyUserToStore,
      postAuthRedirection,
      identifyCompanyUserProduktly,
      notifyCompanyUserPosthog,
    } = useCompanyAuthSession();
    const errorMessage = ref<string | null>(null);
    const isMagicLinkFlow = ref(false);
    const loaderMessage = computed(() =>
      isMagicLinkFlow.value
        ? "Signing you in…"
        : "Loading impersonated session…"
    );
    const errorHeading = computed(() =>
      isMagicLinkFlow.value ? "Sign-in failed" : "Impersonation failed"
    );

    onMounted(async () => {
      const rawToken = route.query.token;
      const token = Array.isArray(rawToken) ? rawToken[0] : rawToken;
      if (!token || typeof token !== "string") {
        errorMessage.value = "No impersonation token provided.";
        return;
      }

      await router.replace({ path: route.path, query: {} });

      const claims = decodeJwtPayload(token);
      const email = (claims.email as string) || "";
      const impersonatedBy = (claims.impersonated_by as string) || "admin";
      isMagicLinkFlow.value = impersonatedBy === "magic_link";

      try {
        // Same endpoint as CognitoCallbackView.exchangeWithRails
        const response = await axios.post(
          process.env.VUE_APP_SIGN_IN_URL as string,
          { user: { token, email }, user_type: "company_user" },
          { headers: { "Content-Type": "application/json" } }
        );

        if (!response.data.token) {
          throw new Error("Server did not return a session token.");
        }

        const data = response.data as {
          token: string;
          email?: string;
          has_company?: boolean;
          personal_info_required?: boolean | null;
          user?: {
            uuid?: string;
            firstName?: string;
            lastName?: string;
            title?: string;
            switchable?: boolean;
          };
          company?: {
            id?: string;
            name?: string;
            website?: string;
            program_linkedin_url?: string;
          };
          add_subscription?: boolean | null;
          custom_payout_fee?: number | null;
        };

        const resolvedEmail = data.email ?? email;
        const resolvedSwitchable =
          data.user?.switchable ?? isMagicLinkFlow.value;

        const user: User = {
          uuid: data.user?.uuid ?? "",
          firstName: data.user?.firstName ?? "",
          lastName: data.user?.lastName ?? "",
          title: data.user?.title ?? "",
          switchable: resolvedSwitchable,
        };

        const company: Company = {
          id: data.company?.id ?? "",
          name: data.company?.name ?? "",
          website: data.company?.website ?? "",
          program_linkedin_url: data.company?.program_linkedin_url ?? "",
        };

        userStore.clearStore();
        applyCompanyUserToStore(
          resolvedEmail,
          user,
          company,
          data.token,
          data.add_subscription ?? null,
          data.custom_payout_fee ?? null
        );
        clearExpertToCompanyHandoffCookies();
        syncSwitchableCompanyHandoffCookie(resolvedEmail, resolvedSwitchable);

        if (impersonatedBy !== "magic_link") {
          userStore.setImpersonated(true);
          userStore.setImpersonatedBy(impersonatedBy);
          const exp = claims.exp as number | undefined;
          if (exp) {
            userStore.setImpersonationExpiresAt(exp);
          }
        }

        identifyCompanyUserProduktly(user.uuid, resolvedEmail);
        notifyCompanyUserPosthog(Boolean(data.personal_info_required));

        postAuthRedirection(
          Boolean(data.has_company),
          Boolean(data.personal_info_required)
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data) {
          const d = err.response.data as Record<string, unknown>;
          errorMessage.value =
            (d.errors as string) ||
            (d.error as string) ||
            (isMagicLinkFlow.value
              ? "Sign-in failed."
              : "Impersonation login failed.");
        } else if (err instanceof Error) {
          errorMessage.value = err.message;
        } else {
          errorMessage.value = "An unexpected error occurred.";
        }
      }
    });

    return { errorMessage, loaderMessage, errorHeading };
  },
});
</script>
